import React, { useEffect, useState } from 'react';
import { fetchWithCorpId } from '../utils/api';
import { useParams } from 'react-router-dom';

function InvoiceForm({ mode = 'create', initialData = {}, onSuccess, onCancel }) {
  const { id } = useParams();
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState('info');
  const [invoice, setInvoice] = useState({
    number: '',
    vendorId: '',
    amount: '',
    invoiceDate: '',
    dueDate: '',
    status: '',
    items: [],
  });
  const [vendors, setVendors] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (isEdit && id) {
        setLoading(true);
        try {
          const data = await fetchWithCorpId(`/invoices/${id}`);
          setInvoice(data);
        } catch (error) {
          console.error('Error fetching invoice:', error);
          setMessage('❌ Error loading invoice data: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchInvoice();
  }, [isEdit, id]);

  // Fetch vendors
  useEffect(() => {
    const fetchData = async () => {
      try {
        const vendorsData = await fetchWithCorpId('/vendors');
        setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError('Failed to load vendors');
      }
    };
    fetchData();
  }, []);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setInvoice(prev => ({
        ...prev,
        ...initialData,
        // Ensure dates are in the correct format
        invoiceDate: initialData.invoiceDate ? new Date(initialData.invoiceDate).toISOString().split('T')[0] : '',
        dueDate: initialData.dueDate ? new Date(initialData.dueDate).toISOString().split('T')[0] : '',
        // Ensure items array is properly initialized
        items: initialData.items || [],
        // Ensure other fields have default values if not provided
        number: initialData.number || '',
        vendorId: initialData.vendorId || '',
        status: initialData.status || '',
        amount: initialData.amount || ''
      }));
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoice(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...(invoice.items || [])];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setInvoice(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    const updatedItems = [...(invoice.items || []), { description: '', quantity: 1, price: 0 }];
    setInvoice(prev => ({ ...prev, items: updatedItems }));
  };

  const removeItem = (index) => {
    const updatedItems = [...(invoice.items || [])];
    updatedItems.splice(index, 1);
    setInvoice(prev => ({ ...prev, items: updatedItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = mode === 'edit' ? `/invoices/${id}` : '/invoices';
      const method = mode === 'edit' ? 'PUT' : 'POST';
      
      // Add corpId from localStorage to the invoice data
      const dataToSubmit = {
        ...invoice,
        corpId: localStorage.getItem('selectedCorpId') || '0'
      };
      
      await fetchWithCorpId(url, {
        method,
        body: JSON.stringify(dataToSubmit)
      });
      
      setMessage(`✅ Invoice ${id ? 'updated' : 'created'} successfully`);
      setTimeout(() => {
        setMessage('');
        onSuccess();
      }, 1000);
    } catch (error) {
      console.error('Error saving invoice:', error);
      setMessage('❌ Failed to save: ' + error.message);
    }
  };

  const calculateAmount = (item) => {
    return (item.quantity || 0) * (item.price || 0);
  };

  const totalAmount = (invoice.items || []).reduce((sum, item) => sum + calculateAmount(item), 0);

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-section">
        <label>Invoice Number:
          <input 
            type="number" 
            name="number" 
            value={invoice.number || ''} 
            readOnly 
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </label>
      </div>

      <div className="form-section">
        <label>Vendor:
          <select name="vendorId" value={invoice.vendorId || ''} onChange={handleChange} required>
            <option value="">Select Vendor</option>
            {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="form-section">
        <label>Amount:
          <input type="number" name="amount" value={invoice.amount || ''} onChange={handleChange} />
        </label>
      </div>

      <div className="form-section">
        <label>Invoice Date:
          <input type="date" name="invoiceDate" value={invoice.invoiceDate || ''} onChange={handleChange} />
        </label>

        <label>Due Date:
          <input type="date" name="dueDate" value={invoice.dueDate || ''} onChange={handleChange} />
        </label>

        <label>Status:
          <select name="status" value={invoice.status || ''} onChange={handleChange}>
            <option value="">Select Status</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Sent">Sent</option>
          </select>
        </label>
      </div>

      <div className="form-section">
        <h3>Line Items</h3>
        <table className="data-table">
          <thead>
            <tr><th>Description</th><th>Quantity</th><th>Price</th><th>Amount</th><th>Action</th></tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, index) => (
              <tr key={index}>
                <td><input value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} /></td>
                <td><input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} /></td>
                <td><input type="number" value={item.price} onChange={(e) => handleItemChange(index, 'price', e.target.value)} /></td>
                <td>{calculateAmount(item).toFixed(2)}</td>
                <td><button type="button" onClick={() => removeItem(index)}>🗑️</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addItem}>+ Add Item</button>
      </div>

      <div className="form-section">
        <strong>Total: ${totalAmount.toFixed(2)}</strong>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          type="submit" 
          style={{ 
            padding: '10px 20px',
            height: '40px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0
          }}
        >
          Save
        </button>
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              padding: '10px 20px',
              height: '40px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {message && <div className="form-message">{message}</div>}
    </form>
  );
}

export default InvoiceForm;
